import React from 'react';
import { PageLoading } from './Spinner';
import EmptyState from './EmptyState';
import {  faFileCircleXmark } from '@fortawesome/free-solid-svg-icons';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  /** Hide this column below the given breakpoint (mobile-friendly grids). */
  hideBelow?: 'sm' | 'md' | 'lg';
}

const HIDE_CLASS: Record<NonNullable<Column<any>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};

export default function DataTable<T extends { id: string | number }>({
  columns,
  rows,
  loading = false,
  emptyTitle = 'No records found',
  emptySubtitle = 'Try adjusting your filters or search terms.',
  onRowClick,
  zebra = true,
  dense = false,
}: {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  onRowClick?: (row: T) => void;
  /** Alternate subtle row background for easier scanning on wide tables. */
  zebra?: boolean;
  /** Tighter vertical padding for data-dense grids. */
  dense?: boolean;
}) {
  if (loading) return <PageLoading />;
  if (!rows.length) return <EmptyState icon={faFileCircleXmark} title={emptyTitle} subtitle={emptySubtitle} />;

 const cellPad = dense ? 'py-0.5' : 'py-2';

  return (
    <div className="-mx-5 rounded-b-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[640px]">
          <thead>
            <tr className="bg-gray-50/80 border-y border-gray-100">
              <th
                className="text-[10.5px] font-bold text-gray-500 uppercase tracking-wider px-5 py-3"
                style={{ width: '70px', textAlign: 'center' }}
              >
                S.No
              </th>

              {columns.map(col => (
                <th
                  key={col.key}
                  className={`text-[10.5px] font-bold text-gray-500 uppercase tracking-wider px-5 py-3 whitespace-nowrap ${col.hideBelow ? HIDE_CLASS[col.hideBelow] : ''
                    }`}
                  style={{ width: col.width, textAlign: col.align || 'left' }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row)}
                className={[
                  'border-b border-gray-50 last:border-0 transition-colors group',
                  onRowClick ? 'cursor-pointer' : '',
                  zebra && i % 2 === 1 ? 'bg-gray-50/40' : 'bg-white',
                  onRowClick ? 'hover:bg-blue-50/40' : '',
                ].join(' ')}
              >
                {/* Serial Number */}
                <td className={`px-5 ${cellPad} text-[13px] text-gray-700 font-medium`}>
                  {i + 1}
                </td>

                {columns.map(col => (
                  <td
                    key={col.key}
                    className={`px-5 ${cellPad} text-[13px] text-gray-700 font-medium align-middle ${col.hideBelow ? HIDE_CLASS[col.hideBelow] : ''
                      }`}
                    style={{ textAlign: col.align || 'left' }}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
